import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { refreshTokensSingleFlight } from '@/services/tokenRefresh';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_superadmin: boolean;
  roles: string[];
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = '/api';

// Race fix 2026-07-08: tokens are only cleared on a DEFINITIVE rejection
// (401 on /auth/me with no refresh path left, or 401/403 from /auth/refresh).
// Transient failures (network errors, 5xx from the edge) NEVER clear storage;
// the old code wiped both tokens on any hiccup, intermittently logging techs
// out on cold loads of /tech. Refresh itself is single-flight and shared with
// the axios interceptor (services/tokenRefresh.ts) because the backend
// refresh token is single-use.
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getStoredTokens = () => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    return { accessToken, refreshToken };
  };

  const setStoredTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  };

  const clearStoredTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  const fetchUser = useCallback(async (allowRefresh: boolean = true, isRetry: boolean = false): Promise<void> => {
    const { accessToken } = getStoredTokens();

    try {
      // Silent SSO bootstrap: call /auth/me with credentials so the browser
      // sends the shared `.concept.bz` portal_jwt cookie. The sso_bridge
      // middleware accepts that cookie and get_current_user resolves the user
      // from it -- so a click from concept.bz/admin lands here ALREADY
      // authenticated, even with no local access_token. If a local Bearer
      // token exists we still send it (covers native-login sessions).
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: accessToken
          ? { 'Authorization': `Bearer ${accessToken}` }
          : {},
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else if (response.status === 401) {
        const { refreshToken: storedRefresh } = getStoredTokens();
        if (allowRefresh && storedRefresh) {
          // Token expired: single-flight refresh, then ONE re-fetch of /auth/me
          // (allowRefresh=false below prevents any refresh loop).
          await doRefresh();
        } else {
          // Definitive 401 with no refresh path -> session is dead.
          if (accessToken) clearStoredTokens();
          setUser(null);
        }
      } else {
        // Non-401 failure (5xx / gateway hiccup): TRANSIENT. Retry once,
        // then give up for this load — but KEEP the stored tokens.
        if (!isRetry) {
          await new Promise((r) => setTimeout(r, 700));
          return fetchUser(allowRefresh, true);
        }
        setUser(null);
      }
    } catch (error) {
      // Network error: TRANSIENT. Never clear tokens here.
      console.error('Error fetching user:', error);
      if (!isRetry) {
        await new Promise((r) => setTimeout(r, 700));
        return fetchUser(allowRefresh, true);
      }
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Shared single-flight refresh; only a DEFINITIVE server rejection of the
  // refresh token clears storage.
  const doRefresh = async (): Promise<void> => {
    const result = await refreshTokensSingleFlight();
    if (result.ok) {
      await fetchUser(false);
      return;
    }
    if (result.definitive) {
      clearStoredTokens();
    }
    setUser(null);
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      setStoredTokens(data.access_token, data.refresh_token);

      // Fetch user data
      await fetchUser();
    } catch (error) {
      clearStoredTokens();
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const { accessToken } = getStoredTokens();

    try {
      if (accessToken) {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // // agentA-clear-portal-cookie
      try {
        document.cookie = 'portal_jwt=; Max-Age=0; Domain=.concept.bz; Path=/; Secure; SameSite=Lax';
        document.cookie = 'portal_jwt=; Max-Age=0; Path=/';
      } catch (_) {}
      clearStoredTokens();
      setUser(null);
    }
  };

  const refreshToken = async () => {
    await doRefresh();
  };

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
